import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslateFunction } from "lang/Translate";

// hooks and utilities
import formatMoney from "common/format";
import usePrevious from "hooks/usePrevious";

// common components
import Tooltip from "components/common/tooltip/Tooltip";

// styles
import "./revenuePopup.scoped.scss";

const RevenuePopup = () => {
    const { open, data, timeout } = useSelector(state => state.revenuePopup);
    const previousTimeout = usePrevious(timeout);
    const dispatch = useDispatch();
    const translate = useTranslateFunction();
    const animationRef = useRef();
    const c = "RevenuePopup";

    const close = () => dispatch({ type: "REVENUE_POPUP_CLOSE" });
    const resetAnimation = el => {
        el.style.animation = "none";
        el.offsetHeight; // eslint-disable-line no-unused-expressions
        el.style.animation = null;
    };

    useEffect(() => {
        if (!animationRef.current) return;
        timeout !== previousTimeout && resetAnimation(animationRef.current);
    }, [timeout, previousTimeout]);

    return (
        <div className={`revenue_popup ${open && "open"}`}>
            <div className="close_countdown" ref={animationRef} />

            <div className="content">
                <div className={`total ${data?.total >= 0 ? "green" : "red"}`}>
                    {data?.total >= 0 ? "+" : "-"} {formatMoney(Math.abs(data?.total))}
                </div>

                <div className="table">
                    {data?.aircraftDeparted != null ? (
                        <div className="row">
                            <div>
                                <i className="fas fa-plane" />
                                <div className="label">{translate("AIRCRAFT_DEPARTED", c)}</div>
                            </div>
                            <div className="amount">{data?.aircraftDeparted}</div>
                        </div>
                    ) : <></>}

                    <div className="row">
                        <div>
                            <i className="fas fa-ticket" />
                            <div className="label">{translate("TICKET_INCOME", c)}</div>
                        </div>
                        <div className="amount green">+ {formatMoney(data?.ticketEarnings)}</div>
                    </div>

                    {data?.dutyFreeEarnings > 0 && (
                        <div className="row">
                            <div>
                                <i className="fas fa-bag-shopping" />
                                <div className="label">{translate("DUTY_FREE_INCOME", c)}</div>
                            </div>
                            <div className="amount green">+ {formatMoney(data.dutyFreeEarnings)}</div>
                        </div>
                    )}

                    {data?.cateringEarnings > 0 && (
                        <div className="row">
                            <div>
                                <i className="fas fa-utensils" />
                                <div className="label">{translate("CATERING_INCOME", c)}</div>
                            </div>
                            <div className="amount green">+ {formatMoney(data.cateringEarnings)}</div>
                        </div>
                    )}

                    <div className="row">
                        <div>
                            <i className="fas fa-users" />
                            <div className="label">{translate("PASSENGERS_ON_BOARD", c)}</div>
                        </div>
                        <div className="amount">
                            <Tooltip
                                text={data?.passengers?.join(" / ") || ""}
                                position="bottom"
                                compact={true}
                                icon={<span>{data?.passengers.reduce((acc, v) => acc + v, 0)}</span>}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div>
                            <i className="fas fa-gas-pump" />
                            <div className="label">{translate("FUEL_USED", c)}</div>
                        </div>
                        <div className="amount">{data?.fuelAmount} lbs</div>
                    </div>

                    <div className="row">
                        <div>
                            <i className="fas fa-gas-pump" />
                            <div className="label">{translate("FUEL_COST", c)}</div>
                        </div>
                        <div className="amount red">- {formatMoney(data?.fuelCost)}</div>
                    </div>

                    <div className="row">
                        <div>
                            <i className="fas fa-receipt" />
                            <div className="label">{translate("OPERATION_FEES", c)}</div>
                        </div>
                        <div className="amount red">- {formatMoney(data?.fixedFees)}</div>
                    </div>
                </div>
            </div>

            <div className="close text_center" onClick={close}>
                <i className="fas fa-angle-up" />
            </div>
        </div>
    );
};

export default RevenuePopup;
